/**
 * Store currency helpers.
 * Never assume USD — always pass the shop’s currency code from Shopify.
 */

/** Locale that formats money sensibly for a given ISO currency */
function localeForCurrency(code: string): string | undefined {
  const c = (code || '').toUpperCase();
  const map: Record<string, string> = {
    INR: 'en-IN',
    USD: 'en-US',
    EUR: 'de-DE',
    GBP: 'en-GB',
    AUD: 'en-AU',
    CAD: 'en-CA',
    JPY: 'ja-JP',
    AED: 'ar-AE',
    SGD: 'en-SG',
    MYR: 'ms-MY',
    PKR: 'en-PK',
    BDT: 'en-BD',
    NPR: 'en-NP',
    LKR: 'en-LK',
  };
  return map[c];
}

export function getCurrencySymbol(currencyCode: string): string {
  const code = (currencyCode || '').toUpperCase();
  if (!code) return '';
  try {
    const parts = new Intl.NumberFormat(localeForCurrency(code) || undefined, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value || code;
  } catch {
    const fallback: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      INR: '₹',
      AUD: 'A$',
      CAD: 'C$',
      JPY: '¥',
      AED: 'د.إ',
      SGD: 'S$',
    };
    return fallback[code] || code;
  }
}

/** Format amount in the store’s currency (e.g. ₹1,250 for INR, not $). */
export function formatMoney(
  amount: number,
  currencyCode: string,
  opts?: { maximumFractionDigits?: number; minimumFractionDigits?: number },
): string {
  const code = (currencyCode || '').toUpperCase();
  if (!code) return String(amount);
  try {
    return new Intl.NumberFormat(localeForCurrency(code) || undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: opts?.maximumFractionDigits ?? 2,
      minimumFractionDigits: opts?.minimumFractionDigits ?? 0,
    }).format(amount);
  } catch {
    return `${getCurrencySymbol(code)}${amount}`;
  }
}

/** TextField prefix for money inputs (₹, $, etc.) */
export function moneyPrefix(currencyCode: string): string {
  return getCurrencySymbol(currencyCode);
}
